import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { apiFetch, ApiError } from '../lib/apiFetch';

export interface AdminUser {
  user_id: number;
  telegram_id: number;
  role: 'super_admin' | 'admin' | 'moderator';
}

const ME_KEY = ['auth', 'me'];

export function useAuth() {
  return useQuery<AdminUser, ApiError>({
    queryKey: ME_KEY,
    queryFn: () => apiFetch<AdminUser>('/api/admin/auth/me'),
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export interface TelegramAuthPayload {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

export function useTelegramLogin() {
  const qc = useQueryClient();
  return useMutation<AdminUser, ApiError, TelegramAuthPayload>({
    mutationFn: (payload) =>
      apiFetch<AdminUser>('/api/admin/auth/telegram', { method: 'POST', body: payload }),
    onSuccess: (data) => {
      // Сохраняем в кеш ТОЛЬКО публичные поля (csrf_token есть в response, но он в cookie — в стейт не нужен)
      qc.setQueryData(ME_KEY, {
        user_id: data.user_id,
        telegram_id: data.telegram_id,
        role: data.role,
      });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, void>({
    mutationFn: async () => {
      await apiFetch('/api/admin/auth/logout', { method: 'POST' });
    },
    onSuccess: () => {
      // Полная очистка кеша — чтобы данные не остались между сессиями разных админов
      qc.clear();
      if (typeof window !== 'undefined') {
        window.location.assign('/login');
      }
    },
  });
}

export function useDevLogin() {
  const qc = useQueryClient();
  return useMutation<AdminUser, ApiError, { password: string }>({
    mutationFn: (payload) =>
      apiFetch<AdminUser>('/api/admin/auth/dev-login', { method: 'POST', body: payload }),
    onSuccess: (data) => {
      qc.setQueryData(ME_KEY, {
        user_id: data.user_id,
        telegram_id: data.telegram_id,
        role: data.role,
      });
    },
  });
}

const ROLE_RANK: Record<AdminUser['role'], number> = {
  moderator: 10,
  admin: 20,
  super_admin: 30,
};

export function hasRank(user: AdminUser | null | undefined, minRank: number): boolean {
  if (!user) return false;
  return ROLE_RANK[user.role] >= minRank;
}
